import React, { useState, useCallback } from "react";
import { Table } from "antd";
import { Resizable } from "react-resizable";
import { FileTextOutlined, CloseCircleOutlined, DollarOutlined, DeleteOutlined } from "@ant-design/icons";
import { numberWithCommas } from "utils/GlobalFunctions";
import { showConfirmDlgWidget } from "components/ConfirmDlgWidget/showConfirmDlgWidget";
import moment from "moment";
import "./RegistrationsTable.css";

const LS_KEY = "registrations_table_col_widths";

const loadWidths = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveWidths = (widths) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(widths));
  } catch {}
};

const ResizableTitle = ({ onResize, width, ...restProps }) => {
  if (!width) return <th {...restProps} />;
  return (
    <Resizable
      width={width}
      height={0}
      handle={<span className="react-resizable-handle" onClick={(e) => e.stopPropagation()} />}
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

function RegistrationsTable(props) {
  const { dataList, onRowClick, onMarkAsPaid, onInvoice, onVoid, onRefund, onDelete, merchant, columnPreferences } = props;

  const [colWidths, setColWidths] = useState(loadWidths);

  const handleResize = useCallback((key) => (_e, { size }) => {
    setColWidths((prev) => {
      const next = { ...prev, [key]: size.width };
      saveWidths(next);
      return next;
    });
  }, []);

  if (dataList === undefined) return <div />;

  const formatDateShort = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format("MM/DD/YY") : value;
  };

  const w = (key, defaultWidth) => colWidths[key] || defaultWidth;

  const columns = [
    {
      title: "Trans ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: w("transaction_id", 90),
      fixed: "left",
      sorter: (a, b) => parseInt(a.transaction_id) - parseInt(b.transaction_id),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("transaction_id") }),
    },
    {
      title: "Parent First",
      dataIndex: "parent_first",
      key: "parent_first",
      width: w("parent_first", 120),
      sorter: (a, b) => `${a.parent_first}`.localeCompare(`${b.parent_first}`),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("parent_first") }),
    },
    {
      title: "Parent Last",
      dataIndex: "parent_last",
      key: "parent_last",
      width: w("parent_last", 120),
      sorter: (a, b) => `${a.parent_last}`.localeCompare(`${b.parent_last}`),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("parent_last") }),
    },
    {
      title: "Email",
      dataIndex: "p_email",
      key: "p_email",
      width: w("p_email", 200),
      sorter: (a, b) => `${a.p_email}`.localeCompare(`${b.p_email}`),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("p_email") }),
    },
    {
      title: "Cell Phone",
      dataIndex: "p_cell_phone",
      key: "p_cell_phone",
      width: w("p_cell_phone", 130),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("p_cell_phone") }),
    },
    {
      title: "Participants",
      dataIndex: "participant_count",
      key: "participant_count",
      width: w("participant_count", 100),
      align: "center",
      sorter: (a, b) => parseInt(a.participant_count) - parseInt(b.participant_count),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("participant_count") }),
    },
    {
      title: "Programs",
      dataIndex: "program_count",
      key: "program_count",
      width: w("program_count", 100),
      align: "center",
      sorter: (a, b) => parseInt(a.program_count) - parseInt(b.program_count),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("program_count") }),
    },
    {
      title: "Total",
      dataIndex: "total_checkout",
      key: "total_checkout",
      width: w("total_checkout", 110),
      sorter: (a, b) => {
        const aTotal = parseFloat(a.total_checkout || 0) + parseFloat(a.linked_total || 0);
        const bTotal = parseFloat(b.total_checkout || 0) + parseFloat(b.linked_total || 0);
        return aTotal - bTotal;
      },
      render: (val, record) => {
        const linked = parseFloat(record.linked_total || 0);
        const base = parseFloat(val || 0);
        const combined = base + linked;
        if (linked > 0) {
          return (
            <span title={`Main: $${numberWithCommas(base.toFixed(2))} + Linked: $${numberWithCommas(linked.toFixed(2))}`}>
              ${numberWithCommas(combined.toFixed(2))} <span style={{ color: "#1890ff", fontSize: 11 }}>+L</span>
            </span>
          );
        }
        return "$" + numberWithCommas(base.toFixed(2));
      },
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("total_checkout") }),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: w("payment_method", 130),
      sorter: (a, b) => `${a.payment_method}`.localeCompare(`${b.payment_method}`),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("payment_method") }),
    },
    {
      title: "Discount Code",
      dataIndex: "discount_code",
      key: "discount_code",
      width: w("discount_code", 120),
      sorter: (a, b) => `${a.discount_code || ""}`.localeCompare(`${b.discount_code || ""}`),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("discount_code") }),
    },
    {
      title: "Reg Date",
      dataIndex: "reg_date",
      key: "reg_date",
      width: w("reg_date", 110),
      sorter: (a, b) => `${a.reg_date}`.localeCompare(`${b.reg_date}`),
      render: (record) => formatDateShort(record),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("reg_date") }),
    },
  ];

  // Insert optional columns based on user preferences
  if (columnPreferences?.columns?.a_park_id?.visible) {
    const regDateIdx = columns.findIndex(c => c.key === "reg_date");
    columns.splice(regDateIdx, 0, {
      title: "Park ID",
      dataIndex: "a_park_id",
      key: "a_park_id",
      width: w("a_park_id", 100),
      sorter: (a, b) => `${a.a_park_id || ""}`.localeCompare(`${b.a_park_id || ""}`),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("a_park_id") }),
    });
  }

  // Add Actions column if any action handlers are provided
  if (onMarkAsPaid || onInvoice || onVoid || onRefund || onDelete) {
    columns.push({
      title: "Actions",
      key: "actions",
      width: w("actions", 280),
      onHeaderCell: (col) => ({ width: col.width, onResize: handleResize("actions") }),
      render: (_, record) => {
        const registrationMerchant = (merchant || '').toLowerCase();
        const paymentMethod = (record.payment_method || '').toLowerCase();
        const paymentGateway = (record.payment_gateway || '').toLowerCase();

        const hasKnownMerchant = registrationMerchant === 'forte' || registrationMerchant === 'stax';
        const canProcessTransaction = hasKnownMerchant || (!registrationMerchant && (paymentGateway === 'forte' || paymentGateway === 'stax' || paymentMethod === 'credit'));

        const rawDate = record.created_at || record.reg_date;
        const transactionDate = rawDate ? new Date(rawDate) : null;
        const today = new Date();
        const isSameDay = transactionDate &&
          transactionDate.getFullYear() === today.getFullYear() &&
          transactionDate.getMonth() === today.getMonth() &&
          transactionDate.getDate() === today.getDate();

        const canVoid = canProcessTransaction && isSameDay && onVoid;
        const canRefund = canProcessTransaction && onRefund;

        return (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {onInvoice && (
              <a
                style={{ color: "#1890ff" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onInvoice(record.transaction_id, record, onMarkAsPaid ? "invoice" : "receipt");
                }}
              >
                <FileTextOutlined /> {onMarkAsPaid ? "Invoice" : "Receipt"}
              </a>
            )}
            {onMarkAsPaid && (
              <a
                style={{ color: "#52c41a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsPaid(record.transaction_id);
                }}
              >
                Mark as Paid
              </a>
            )}
            {canVoid && (
              <a
                style={{ color: "#ff4d4f" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onVoid(record);
                }}
              >
                <CloseCircleOutlined /> Void
              </a>
            )}
            {canRefund && (
              <a
                style={{ color: "#ff7a00" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefund(record);
                }}
              >
                <DollarOutlined /> Refund
              </a>
            )}
            {onDelete && (
              <a
                style={{ color: "red" }}
                onClick={async (e) => {
                  e.stopPropagation();
                  var res = await showConfirmDlgWidget({
                    title: "Delete Confirmation",
                    content: "Are you sure you want to delete this transaction and its participants?",
                  });
                  if (res === false) return;
                  onDelete(record.transaction_id);
                }}
              >
                <DeleteOutlined /> Delete
              </a>
            )}
          </div>
        );
      },
    });
  }

  const dataSource =
    dataList.length === 0
      ? []
      : dataList.map((data, index) => ({ ...data, key: index }));

  return (
    <div className="info-list-widget">
      <Table
        components={{ header: { cell: ResizableTitle } }}
        columns={columns}
        dataSource={dataSource}
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        onRow={(record) => ({
          onClick: () => onRowClick && onRowClick(record),
          style: onRowClick ? { cursor: "pointer" } : {},
        })}
        scroll={{ x: 1400 }}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
}

export default RegistrationsTable;
